<template>
    <div class="tech-areas-tab mt-xl h-100">
        <p class="my-md ml-sm">{{ description }}</p>
        <AonCoverLoading :loading="loading" title="Loading Technology Areas..."></AonCoverLoading>
        <div class="display-flex mb-lg">
            <AonButton
                class="table-button"
                :class="{ 'active-table-button': activeSideBarType === 'filters' }"
                label="Filters"
                text-with-icon
                type="subtle"
                :icon-options="{ iconStyle: 'fas', iconName: 'fa-bars-filter' }"
                @clicked="() => toggleSideBar('filters', gridApi)"
            />
            <AonButton
                class="ml-md table-button"
                :class="{ 'active-table-button': activeSideBarType === 'columns' }"
                label="Columns"
                text-with-icon
                type="subtle"
                :icon-options="{ iconStyle: 'fas', iconName: 'fa-objects-column' }"
                @clicked="() => toggleSideBar('columns', gridApi)"
            />
            <div
                class="clear-filters display-flex align-items-center cursor-pointer p-sm ml-sm"
                @click="clearFilters"
            >
                <font-awesome-icon :icon="`fas fa-xmark`" class="text-moat-600" />
                <p class="geist-font text-moat-600 bold ml-xs">Clear Filters</p>
            </div>
        </div>
        <MoatTable
            :loading="loading"
            style="width: 100%; height: 850px"
            :class="themeClass"
            :column-defs="colDefs"
            :row-data="tableData"
            :side-bar="sideBar"
            :tooltip-show-delay="500"
            :is-external-filter-present="shouldApplyQuickFilter"
            :does-external-filter-pass="applyQuickFilter"
            pagination
            @grid-ready="gridInit"
            @row-data-updated="syncTechAreaFilters"
        />
    </div>
</template>

<script setup>
import { ref, computed, onMounted, inject, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useEntityStore, useMoat2ProductStore } from '@/stores'

import { getEntityNodeMetrics } from '@/api/ipNode.js'
import { AgGridVue as MoatTable } from '@ag-grid-community/vue3'
import { formatTpeRange } from '@/lib/financial.js'
import { getInvestmentGroup } from '@/lib/investments'
import { applyQuickFilter as benchmarkingFilter } from '@/lib/benchmarking'
import { useFlag } from '@unleash/proxy-client-vue'
import { usePatentViewerStore } from '@/stores'
import { useAgGridSidebar } from '@/composables/useAgGridSidebar.js'

const logger = inject('logger')
const filters = inject('filters')
const { t } = useI18n()
const entityStore = useEntityStore()
const moat2ProductStore = useMoat2ProductStore()
const patentViewerStore = usePatentViewerStore()

const isProUser = useFlag('ipAlpha.proUsers')

const props = defineProps({
    quickFilter: {
        type: String,
        default: 'all',
    },
    selectedWatchlist: {
        type: Number,
        default: null,
    },
})

const loading = ref(true)
const gridApi = ref(null)
const themeClass = ref('ag-theme-quartz')
const tableData = ref([])

const description = computed(() => {
    return `Compare performance metrics between companies in each product and technology area, including Moat Coverage, momentum, and implied innovation implied P/E.`
})

const {
    sideBar,
    activeSideBarType,
    toggleSideBar,
    injectSidebarHeaders,
    clearFilters: sidebarClearFilters,
} = useAgGridSidebar({
    panels: [
        {
            id: 'columns',
            label: 'Columns',
            width: 275,
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
                suppressRowGroups: true,
                suppressValues: true,
                suppressPivotMode: true,
            },
        },
        {
            id: 'filters',
            label: 'Filters',
            width: 275,
            toolPanel: 'agFiltersToolPanel',
        },
    ],
    injectHeaders: {
        filters: 'Filters',
        columns: 'Columns',
    },
    position: 'left',
})

const colDefs = computed(() => [
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            tooltipDefintion: t('productAlignment.definitionTooltips.companyName'),
            headerName: 'Company Name',
            showFilterButton: true,
        },
        cellRenderer: 'MTCustomCellCompany',
        cellRendererParams: {
            allowNewTab: true,
            tabOnLeft: true,
        },
        field: 'entityName',
        headerName: 'Company Name',
        headerTooltip: 'Company Name',
        minWidth: 300,
        flex: 1,
        sortingOrder: ['asc', 'desc', null],
        filter: 'agSetColumnFilter',
        pinned: 'left',
        lockPosition: true,
        chartDataType: 'category',
    },
    {
        headerName: 'Product',
        headerTooltip: 'Product',
        valueGetter: (p) => {
            if (p.data) {
                return p.data.ultimateConceptDisplayName
            }
        },
        field: 'ultimateConceptDisplayName',
        filter: 'agSetColumnFilter',
        minWidth: 400,
    },
    {
        field: 'displayName',
        headerName: 'Technology Area',
        minWidth: 400,
        filter: 'agSetColumnFilter',
    },

    {
        field: 'entityType',
        headerName: 'Company Type',
        minWidth: 200,
        filter: 'agTextColumnFilter',
        hide: true,
    },
    {
        field: 'isCompetitor',
        headerName: 'Is Competitor',
        minWidth: 150,
        filter: 'agTextColumnFilter',
        hide: true,
        suppressColumnsToolPanel: true,
    },
    {
        field: 'isAcquisitionTarget',
        headerName: 'Is Acquisition Target',
        minWidth: 150,
        filter: 'agTextColumnFilter',
        hide: true,
        suppressColumnsToolPanel: true,
    },
    {
        field: 'isPotentialBuyer',
        headerName: 'Is Potential Buyer',
        minWidth: 150,
        filter: 'agTextColumnFilter',
        hide: true,
        suppressColumnsToolPanel: true,
    },
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            tooltipDefinition: t('productAlignment.definitionTooltips.innovationScore'),
            headerName: 'Moat Coverage',
        },
        field: 'powerScore',
        headerName: 'Moat Coverage',
        valueFormatter: (p) => {
            if (p.data) {
                return `${parseInt(p.data.powerScore.toFixed(0)).toLocaleString()}`
            }
        },
        minWidth: 220,
        filter: 'agNumberColumnFilter',
    },
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            tooltipDefinition: t('productAlignment.definitionTooltips.opportunity'),
            headerName: 'Momentum',
        },
        field: 'opportunity',
        headerName: 'Momentum',
        valueFormatter: (p) => {
            if (p.data) {
                return Math.round(p.data.opportunity)
            }
        },
        headerTooltip: 'Momentum',
        minWidth: 150,
        flex: 1,
        filter: 'agNumberColumnFilter',
    },
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            tooltipDefinition: t('productAlignment.definitionTooltips.innImpliedPE'),
            headerName: 'II P/E',
        },
        field: 'tpe',
        headerName: 'II P/E',
        headerTooltip: 'Innovation Implied P/E',
        minWidth: 120,
        flex: 1,
        valueFormatter: (p) => {
            if (p.data) {
                return formatTpeRange(p.data.tpe, p.data.tpeLb, p.data.tpeUb)
            }
        },
        filter: 'agNumberColumnFilter',
    },
    {
        field: 'hqCountryName',
        headerName: 'HQ Country',
        hide: true,
        filter: 'agSetColumnFilter',
        chartDataType: 'excluded',
    },
    {
        field: 'totalRevenue',
        headerName: 'Revenue',
        hide: true,
        filter: 'agNumberColumnFilter',
        filterParams: {
            filterOptions: [
                'equals',
                'notEqual',
                'lessThan',
                'lessThanOrEqual',
                'greaterThan',
                'greaterThanOrEqual',
                'inRange',
            ],
            defaultOption: 'greaterThan',
        },
        chartDataType: 'excluded',
        valueFormatter: (p) => {
            if (p.data) {
                return filters.abbreviate(p.data.totalRevenue)
            }
        },
    },
    {
        field: 'employeeCount',
        headerName: 'Employee Count',
        hide: true,
        filter: 'agSetColumnFilter',
        chartDataType: 'excluded',
    },
    {
        field: 'priceToEarnings',
        headerName: 'Price to Earnings',
        hide: true,
        filter: 'agNumberColumnFilter',
        chartDataType: 'excluded',
    },
    {
        field: 'yoyRevenueGrowthPct',
        headerName: 'YoY Revenue Growth %',
        hide: true,
        filter: 'agNumberColumnFilter',
    },
    {
        field: 'marketValue',
        headerName: 'Market Cap',
        hide: true,
        filter: 'agNumberColumnFilter',
    },
    {
        field: 'currency',
        headerName: 'Currency',
        hide: true,
        filter: 'agSetColumnFilter',
    },
    {
        field: 'lastFundingRoundInvestmentType',
        headerName: 'Latest Funding Round',
        hide: true,
        valueGetter: (p) => {
            if (p.data) {
                return getInvestmentGroup(p.data.lastFundingRoundInvestmentType)
            }
        },
        filter: 'agSetColumnFilter',
    },
    ...(isProUser.value
        ? [
              {
                  suppressHeaderMenuButton: true,
                  cellRenderer: 'MTContextMenu',
                  cellRendererParams: {
                      actionsList: [
                          {
                              name: 'View Company Patents',
                              icon: 'fa-square-list',
                              actionName: 'patents',
                              patentsAction: async (params) => {
                                  patentViewerStore.nodeIdList = [params.data.nodeId]
                                  patentViewerStore.technologyName = `${params.data.ultimateConceptDisplayName} - ${params.data.displayName}`
                                  patentViewerStore.entityPkList = [params.data.entityPk]
                                  patentViewerStore.entityName = params.data.entityName
                                  await nextTick()
                                  patentViewerStore.showPatentViewerList = true
                              },
                          },
                      ],
                      highlightLabel: true,
                  },
                  pinned: 'right',
                  width: 60,
              },
          ]
        : []),
])

const handleQuickFilter = (filter) => {
    // Reset any existing filters
    gridApi.value.setFilterModel(null)

    // Update the grid with the new filter
    gridApi.value.onFilterChanged()
}

// Watch for filter changes
watch(
    () => props.quickFilter,
    (newVal) => {
        if (gridApi.value) {
            handleQuickFilter(newVal)
        }
    }
)

// Watch for watchlist changes
watch(
    () => props.selectedWatchlist,
    (newVal) => {
        if (gridApi.value) {
            fetchData()
        }
    }
)

const gridInit = (params) => {
    gridApi.value = params.api
    injectSidebarHeaders()

    // Set initial sort on Moat Coverage
    const columnState = gridApi.value.getColumnState()
    columnState.forEach((col) => {
        col.sort = null
        col.sortIndex = null
    })

    // Apply default sort
    const ultimateDisplayNameCol = columnState.find(
        (col) => col.colId === 'ultimateConceptDisplayName'
    )
    if (ultimateDisplayNameCol) {
        ultimateDisplayNameCol.sort = 'asc'
        ultimateDisplayNameCol.sortIndex = 0
    }

    const displayNameCol = columnState.find((col) => col.colId === 'displayName')
    if (displayNameCol) {
        displayNameCol.sort = 'asc'
        displayNameCol.sortIndex = 1
    }

    const powerScoreCol = columnState.find((col) => col.colId === 'powerScore')
    if (powerScoreCol) {
        powerScoreCol.sort = 'desc'
        powerScoreCol.sortIndex = 2
    }

    gridApi.value.applyColumnState({ state: columnState, defaultState: { sort: null } })
    fetchData()
}

const fetchData = async () => {
    loading.value = true

    try {
        const params = {
            targetEntityPk: entityStore.entity?.aon_entity_pk,
            page_size: 100000,
        }

        // Add selected watchlist if available
        if (props.selectedWatchlist) {
            params.datasetPk = props.selectedWatchlist
        }

        // Fetch all data at once (no pagination parameters)
        const nodeId = moat2ProductStore.rootNodeId
        const response = await getEntityNodeMetrics(nodeId, params)
        if (response.data.results.length === 0) {
            gridApi.value.showNoRowsOverlay()
        } else {
            tableData.value = response.data.results
            gridApi.value.hideOverlay()
        }
    } catch (error) {
        logger.error('Error fetching tech areas data:', error)
        gridApi.value.showNoRowsOverlay()
    } finally {
        loading.value = false
    }
}

const applyQuickFilter = (node) => {
    return benchmarkingFilter(node.data, props.quickFilter)
}

const shouldApplyQuickFilter = () => {
    return props.quickFilter !== 'all'
}

const getTechAreaNames = (selectedTechAreas) => {
    if (!selectedTechAreas || selectedTechAreas.size === 0) return []
    return Array.from(selectedTechAreas).map((techArea) => techArea.displayName)
}

const syncTechAreaFilters = async () => {
    if (gridApi.value) {
        const filterInstance = {
            filterType: 'set',
            values: getTechAreaNames(moat2ProductStore.productAlignmentTechAreaSelections),
        }

        await gridApi.value.setColumnFilterModel('displayName', filterInstance)

        gridApi.value.onFilterChanged()
    }
}

watch(
    () => moat2ProductStore.productAlignmentTechAreaSelections,
    (newVal, oldVal) => {
        syncTechAreaFilters()
    },
    { immediate: true, deep: true }
)

const clearFilters = () => {
    if (gridApi.value) {
        sidebarClearFilters(gridApi.value)
    }
}
</script>

<style lang="scss" scoped>
.tech-areas-tab {
    height: 100%;
    position: relative;
}

.ag-theme-quartz {
    --ag-checkbox-checked-color: #2a79d2;
    --ag-font-family: 'Geist', sans-serif;
    --ag-font-size: 14px;

    :deep(.ag-header-cell-text) {
        font-family: 'Geist', sans-serif;
        font-weight: 800;
    }

    :deep(.ag-cell) {
        font-family: 'Geist', sans-serif;
    }
}
</style>
